<template>
  <v-container class="pa-0" fluid>
    <v-progress-linear
      v-show="isLoading || isGeneratingPDF"
      indeterminate
      color="blue"
    />
    <div v-if="!isLoading" class="d-flex justify-center">
      <div
        style="width:816px"
        class="d-flex justify-end align-center pt-8 pr-2"
      >
        <v-btn
          color="primary"
          data-testid="psPrint-button-download"
          plain
          :loading="isGeneratingPDF"
          @click="downloadPdf"
        >
          {{ $t('problem_solving.print.download_as_pdf') }}
          <v-icon right>
            mdi-arrow-collapse-down
          </v-icon>
        </v-btn>
      </div>
    </div>
    <div v-if="!isLoading" class="d-flex align-center flex-column mt-6">
      <VirtualPage ref="mainPage">
        <template slot="content">
          <MainPagePrint :rev="rev" />
        </template>
      </VirtualPage>
      <template>
        <div v-for="(causes, index) in rootCausesChunk" :key="index">
          <VirtualPage ref="rootCauses">
            <template slot="content">
              <RootCausesPagePrint :rev="rev" :rootCauses="causes" />
            </template>
          </VirtualPage>
        </div>
      </template>

      <VirtualPage ref="reviewPage">
        <template slot="content">
          <ReviewPage :rev="rev" />
        </template>
      </VirtualPage>
    </div>
  </v-container>
</template>

<script>
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import _ from 'lodash'

import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import { createLetterPdf } from '@/lib/pdf'
import chunkRootCauses from './chunkRootCauses'
import MainPagePrint from './Pages/MainPagePrint.vue'
import VirtualPage from '@/components/VirtualLetterPortraitPage.vue'
import ReviewPage from './Pages/ReviewPrint.vue'
import RootCausesPagePrint from './Pages/RootCausesPagePrint.vue'

const MAX_CHARACTERS = {
  paragraph: 300,
  name: 95,
  rootCauseField: 900,
}

const PNG_FORMAT = 'PNG'

export default {
  name: 'ProblemSolvingPrintView',

  mixins: [mixin, trackerMixin],

  components: {
    MainPagePrint,
    VirtualPage,
    ReviewPage,
    RootCausesPagePrint,
  },

  data() {
    return {
      isTranslated: this.$route?.query?.isTranslated,
      organizationId: this.$route.params?.organizationId ?? '',
      revId: this.$route.params?.revId ?? '',
      isLoading: true,
      rev: {},
      revChallenge: {},
      isGeneratingPDF: false,
      MAX_CHARACTERS,
    }
  },

  mounted() {
    this.getRevInfo()
    this.getChallengeInfo()
  },

  computed: {
    rootCausesChunk() {
      return chunkRootCauses(this.rev.rootCauses, 1)
    },
  },

  methods: {
    getFieldsToTranslate(rev) {
      const fieldsToTranslate = [
        'name',
        'problemAnalysis.problem',
        'problemAnalysis.containment',
        'problemAnalysis.goals',
        'problemAnalysis.businessRequirements',
        'solutionDescription',
      ]
      rev?.rootCauses?.forEach?.((rootCause, i) => {
        fieldsToTranslate.push(
          `rootCauses[${i}].name`,
          `rootCauses[${i}].description`,
          `rootCauses[${i}].type`,
          `rootCauses[${i}].correctiveAction`,
          `rootCauses[${i}].preventiveAction`,
          `rootCauses[${i}].correctiveLearning`
        )
      })
      return fieldsToTranslate
    },

    async getRev() {
      const response = await api.problemsolving.getById(
        this.organizationId,
        this.revId
      )
      this.rev = response?.data ?? {}
      this.trackPrint(this.rev, 'Print view')
    },

    async getChallengeInfo() {
      const response = await api.challenge.getChallengesFromRev(
        'problem-solving',
        this.organizationId,
        this.revId
      )
      this.revChallenge = response.data?.[0] ?? {}
    },

    async translateRev() {
      const targetLanguage = this.$store.getters.language
      const fieldsToTranslate = this.getFieldsToTranslate(this.rev)
      const translatedRev = {}

      for await (const field of fieldsToTranslate) {
        if (!_.get(this.rev, field)) continue
        const translatedField = await api.translate.text(
          this.organizationId,
          _.get(this.rev, field),
          targetLanguage
        )
        _.set(translatedRev, field, translatedField.data.translatedText)
      }

      const translatedRootCauses = this.rev.rootCauses.map((cause, i) => {
        return {
          ...cause,
          ...translatedRev.rootCauses[i],
        }
      })

      this.rev = {
        ...this.rev,
        name: translatedRev.name,
        solutionDescription: translatedRev.solutionDescription,
        problemAnalysis: {
          ...this.rev.problemAnalysis,
          ...translatedRev.problemAnalysis,
        },
        rootCauses: translatedRootCauses,
      }
    },

    async getCanvas(refsList) {
      const results = []
      for await (const element of refsList) {
        const canvasLocal = await html2canvas(element, {
          type: 'dataURL',
          scale: 2,
          foreignObjectRendering: false,
        })
        results.push(canvasLocal)
      }
      return results
    },

    async getRevInfo() {
      if (!this.organizationId || !this.revId) return
      await this.getRev()

      try {
        if (this.isTranslated) {
          await this.translateRev()
        }
      } catch (e) {
        this.showError(e)
      } finally {
        this.isLoading = false
      }
    },

    async downloadPdf() {
      try {
        this.isGeneratingPDF = true
        await this.$nextTick()
        const rootCausesRefs =
          this.$refs?.rootCauses?.map(cause => cause.$el) ?? []
        const refsList = _.compact([
          this.$refs.mainPage.$el,
          ...rootCausesRefs,
          this.$refs.reviewPage.$el,
        ])
        const canvasList = await this.getCanvas(refsList)
        const doc = createLetterPdf('p', jsPDF)
        const width = doc.internal.pageSize.getWidth()
        const height = doc.internal.pageSize.getHeight()
        canvasList.forEach((canvas, index) => {
          doc.setPage(index + 1)
          doc.addImage(
            canvas,
            PNG_FORMAT,
            0,
            0,
            width,
            height,
            undefined,
            'FAST'
          )
          if (index < canvasList.length - 1) {
            doc.addPage('p', 'mm', 'letter')
          }
        })

        doc.save(`${this.rev.name}-print.pdf`)
        this.trackPrint(this.rev, 'Print download')
      } catch (err) {
        this.showError(err.message)
      } finally {
        this.isGeneratingPDF = false
      }
    },
  },
}
</script>
